import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaBuilding,
  FaGlobe,
  FaCalendarAlt,
  FaSyncAlt,
  FaExclamationTriangle
} from 'react-icons/fa';
import {
  DATE_PRESETS,
  getPresetDateRange,
  isDateRangeValid,
  buildUtcDayRange,
  getTodayIsoDate,
  loadAnalyticsSiteCollections,
  useAnalyticsOrganizations
} from '../shared/analyticsViewUtils';
import './AnalyticsFilterBar.css';

/**
 * Shared filter toolbar for the IAP Usage Analytics Hub.
 * Manages organization, site collection, date presets, and UTC date bounds.
 */
export default function AnalyticsFilterBar({
  orgId,
  roles = [],
  onFilterChange,
  onRefresh,
  isLoading = false,
  disabled = false,
  defaultPreset = '7d'
}) {
  const today = getTodayIsoDate();

  // Organizations (System Admin scope)
  const {
    isSystemAdmin,
    organizations,
    selectedOrg,
    setSelectedOrg,
    orgsLoading
  } = useAnalyticsOrganizations(roles, orgId);

  // Site Collections
  const [siteCollections, setSiteCollections] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState(null);

  // Date Range & Presets
  const [activePreset, setActivePreset] = useState(defaultPreset);
  const initialRange = getPresetDateRange(defaultPreset) || {
    startDate: today,
    endDate: today
  };
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);

  const lastEmittedRef = useRef(null);
  const fetchSitesAbortRef = useRef(null);

  const fetchSites = useCallback(async () => {
    if (fetchSitesAbortRef.current) {
      fetchSitesAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchSitesAbortRef.current = controller;

    setSelectedSite('');
    setSiteCollections([]);

    if (!selectedOrg) {
      setSitesLoading(false);
      return;
    }

    await loadAnalyticsSiteCollections({
      orgId: selectedOrg,
      setSitesLoading,
      setSitesError,
      setSiteCollections,
      setSelectedSite,
      signal: controller.signal
    });
  }, [selectedOrg]);

  useEffect(() => {
    fetchSites();
    return () => {
      if (fetchSitesAbortRef.current) {
        fetchSitesAbortRef.current.abort();
      }
    };
  }, [fetchSites]);

  // Handle Preset Click
  const handlePresetClick = (presetId) => {
    if (disabled || isLoading) return;
    setActivePreset(presetId);
    if (presetId !== 'custom') {
      const range = getPresetDateRange(presetId);
      if (range) {
        setStartDate(range.startDate);
        setEndDate(range.endDate);
      }
    }
  };

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    setStartDate(val);
    setActivePreset('custom');
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    setEndDate(val);
    setActivePreset('custom');
  };

  const isValidDateRange = isDateRangeValid(startDate, endDate);
  const isSiteValid = siteCollections.some((s) => String(s.id) === String(selectedSite));
  const isFormValid =
    Boolean(selectedOrg) &&
    Boolean(selectedSite) &&
    isSiteValid &&
    isValidDateRange &&
    !sitesLoading;

  useEffect(() => {
    if (!selectedOrg) return;

    if (!isFormValid) {
      if (lastEmittedRef.current && lastEmittedRef.current.organizationId !== selectedOrg) {
        const resetPayload = {
          organizationId: selectedOrg,
          siteCollectionId: '',
          startDate,
          endDate,
          fromUtc: null,
          toUtc: null,
          preset: activePreset
        };
        lastEmittedRef.current = resetPayload;
        onFilterChange?.(resetPayload);
      }
      return;
    }

    const { startDate: fromUtc, endDate: toUtc } = buildUtcDayRange(startDate, endDate);

    const isSameAsLast =
      lastEmittedRef.current &&
      lastEmittedRef.current.organizationId === selectedOrg &&
      lastEmittedRef.current.siteCollectionId === selectedSite &&
      lastEmittedRef.current.fromUtc === fromUtc &&
      lastEmittedRef.current.toUtc === toUtc &&
      lastEmittedRef.current.preset === activePreset;

    if (!isSameAsLast) {
      const payload = {
        organizationId: selectedOrg,
        siteCollectionId: selectedSite,
        startDate,
        endDate,
        fromUtc,
        toUtc,
        preset: activePreset
      };
      lastEmittedRef.current = payload;
      onFilterChange?.(payload);
    }
  }, [selectedOrg, selectedSite, startDate, endDate, activePreset, isFormValid, onFilterChange]);

  const handleRefreshClick = () => {
    if (!isFormValid || isLoading || disabled) return;
    onRefresh?.();
  };

  return (
    <div className="analytics-filter-bar" data-testid="analytics-filter-bar">
      <div className="filter-bar-primary-row">
        {/* Scope Group: Organization (System Admin) + Site Collection */}
        <div className="filter-scope-group">
          {isSystemAdmin && (
            <div className="filter-item filter-org-select">
              <label htmlFor="filter-org-select">
                <FaBuilding className="filter-label-icon" /> Organization
              </label>
              <select
                id="filter-org-select"
                aria-label="Select Organization"
                value={selectedOrg}
                onChange={(e) => {
                  if (fetchSitesAbortRef.current) {
                    fetchSitesAbortRef.current.abort();
                  }
                  setSelectedOrg(e.target.value);
                  setSelectedSite('');
                  setSiteCollections([]);
                }}
                disabled={orgsLoading || disabled}
              >
                <option value="">Select Organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name || org.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-item filter-site-select">
            <label htmlFor="filter-site-select">
              <FaGlobe className="filter-label-icon" /> Site Collection
            </label>
            <select
              id="filter-site-select"
              aria-label="Select Site Collection"
              value={selectedSite}
              onChange={(e) => {
                setSelectedSite(e.target.value);
                try {
                  localStorage.setItem('selected_site_collection_id', e.target.value);
                } catch (e) { }
              }}
              disabled={sitesLoading || siteCollections.length === 0 || disabled}
            >
              {sitesLoading ? (
                <option value="">Loading sites...</option>
              ) : siteCollections.length === 0 ? (
                <option value="">No Sites</option>
              ) : (
                siteCollections.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name || site.id}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Date Presets Pills */}
        <div className="filter-presets-group" role="group" aria-label="Date Range Presets">
          <span className="presets-label">
            <FaCalendarAlt className="filter-label-icon" /> Range:
          </span>
          <div className="preset-pills">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`preset-pill ${activePreset === p.id ? 'active' : ''}`}
                onClick={() => handlePresetClick(p.id)}
                disabled={disabled}
                data-testid={`preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Date Inputs */}
        <div className="filter-date-inputs-group">
          <div className="filter-date-item">
            <label htmlFor="filter-start-date">From</label>
            <input
              type="date"
              id="filter-start-date"
              aria-label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              max={endDate || today}
              disabled={disabled}
            />
          </div>
          <div className="filter-date-item">
            <label htmlFor="filter-end-date">To</label>
            <input
              type="date"
              id="filter-end-date"
              aria-label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              min={startDate}
              max={today}
              disabled={disabled}
            />
          </div>
        </div>

        {/* Action Button: Refresh */}
        <div className="filter-action-group">
          <button
            type="button"
            className="filter-refresh-btn"
            onClick={handleRefreshClick}
            disabled={!isFormValid || isLoading || disabled}
            aria-label="Refresh Analytics Data"
            data-testid="filter-refresh-btn"
          >
            <FaSyncAlt className={`refresh-icon ${isLoading ? 'spinning' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Validation & Warning Notices */}
      {!isValidDateRange && (
        <div className="filter-bar-notice filter-error-notice" role="alert">
          <FaExclamationTriangle className="notice-icon" />
          <span>
            {startDate && endDate && new Date(startDate) > new Date(endDate)
              ? 'Start date must be on or before end date.'
              : 'Maximum date range is 30 days.'}
          </span>
        </div>
      )}

      {sitesError && (
        <div className="filter-bar-notice filter-warning-notice" role="alert">
          <FaExclamationTriangle className="notice-icon" />
          <span>{sitesError}</span>
        </div>
      )}
    </div>
  );
}

AnalyticsFilterBar.propTypes = {
  orgId: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  onFilterChange: PropTypes.func,
  onRefresh: PropTypes.func,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  defaultPreset: PropTypes.string
};
